import {
  getWencesApiBaseUrl,
  getWencesApiAuthHeaders,
} from "@/api/wencesApi";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fallos típicos de red o stream cortado: reintentar el POST completo suele resolverlo
 * (el servidor vuelve a abrir sesión MEV en cada intento).
 */
function isTransientNdjsonFailure(err) {
  if (!err || err.name === "AbortError") return false;
  if (err.ndjsonTransientHttp === true) return true;
  const msg = String(err.message || "").toLowerCase();
  if (msg.includes("network error")) return true;
  if (msg.includes("failed to fetch")) return true;
  if (msg.includes("load failed")) return true;
  if (msg.includes("network request failed")) return true;
  if (msg.includes("respuesta incompleta del servidor")) return true;
  if (msg.includes("sin cuerpo de respuesta")) return true;
  if (msg.includes("error leyendo la respuesta")) return true;
  if (err instanceof TypeError && msg.includes("fetch")) return true;
  return false;
}

/**
 * POST con body.stream=true; si la respuesta es NDJSON, emite onStatus por línea
 * y devuelve el payload final de { type: 'result', data }.
 * Reintenta ante cortes de red / stream incompleto (opciones maxRetries, retryDelayMs).
 */
export async function postJurisprudenciaNdjson(
  path,
  jsonBody,
  { signal, onStatus, maxRetries = 2, retryDelayMs = 1000 } = {}
) {
  const attempts = Math.max(0, Number(maxRetries) || 0) + 1;
  const baseDelay = Math.max(200, Number(retryDelayMs) || 1000);
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    if (signal && signal.aborted) {
      const e = new Error("Aborted");
      e.name = "AbortError";
      throw e;
    }
    try {
      return await postJurisprudenciaNdjsonOnce(path, jsonBody, {
        signal,
        onStatus,
      });
    } catch (err) {
      lastErr = err;
      const retryable = i < attempts - 1 && isTransientNdjsonFailure(err);
      if (!retryable) throw err;
      await sleep(baseDelay * (i + 1));
    }
  }
  throw lastErr;
}

async function postJurisprudenciaNdjsonOnce(
  path,
  jsonBody,
  { signal, onStatus } = {}
) {
  const base = getWencesApiBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${p}`;
  const res = await fetch(url, {
    method: "POST",
    headers: getWencesApiAuthHeaders(),
    body: JSON.stringify({ ...jsonBody, stream: true }),
    signal,
  });

  const ct = (res.headers.get("content-type") || "").toLowerCase();

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j && (j.error != null || j.message != null)) {
        msg = String(j.error != null ? j.error : j.message);
      }
    } catch {
      try {
        const t = await res.text();
        if (t && t.trim()) msg = t.trim().slice(0, 500);
      } catch {
        /* ignore */
      }
    }
    const err = new Error(msg);
    if ([502, 503, 504].includes(res.status)) {
      err.ndjsonTransientHttp = true;
    }
    throw err;
  }

  if (!ct.includes("ndjson") && !ct.includes("x-ndjson")) {
    const data = await res.json();
    return { data, streamed: false };
  }

  const reader = res.body && res.body.getReader();
  if (!reader) {
    throw new Error("Sin cuerpo de respuesta");
  }

  const dec = new TextDecoder();
  let buf = "";
  let finalData = null;

  try {
    while (true) {
      let chunk;
      try {
        chunk = await reader.read();
      } catch (readErr) {
        const m = readErr && readErr.message ? String(readErr.message) : "";
        throw new Error(
          m ? `Error leyendo la respuesta: ${m}` : "Error leyendo la respuesta"
        );
      }
      const { done, value } = chunk;
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() || "";
      for (const line of lines) {
        if (!line.trim()) continue;
        let obj;
        try {
          obj = JSON.parse(line);
        } catch {
          continue;
        }
        if (obj.type === "status" && typeof onStatus === "function") {
          onStatus(obj);
        }
        if (obj.type === "result") finalData = obj.data;
        if (obj.type === "error") {
          throw new Error(obj.message || "Error");
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }

  if (buf.trim()) {
    try {
      const obj = JSON.parse(buf);
      if (obj.type === "status" && typeof onStatus === "function") {
        onStatus(obj);
      }
      if (obj.type === "result") finalData = obj.data;
      if (obj.type === "error") {
        throw new Error(obj.message || "Error");
      }
    } catch (e) {
      if (e instanceof Error && e.message && e.message !== "Error") throw e;
    }
  }

  if (!finalData) {
    throw new Error("Respuesta incompleta del servidor");
  }
  return { data: finalData, streamed: true };
}

function jurisErrorBlobFromUnknown(err) {
  if (!err) return "";
  const parts = [];
  if (err.message) parts.push(String(err.message));
  try {
    const c = err.cause;
    if (c && c.message) parts.push(String(c.message));
  } catch {
    /* ignore */
  }
  if (err.response != null) {
    const st = err.response.status;
    if (st != null) parts.push(String(st));
    const d = err.response.data;
    if (d && typeof d === "object") {
      if (d.error != null) parts.push(String(d.error));
      if (d.message != null) parts.push(String(d.message));
    }
  }
  return parts.join(" ").toLowerCase();
}

function jurisMessageLooksLikeInfrastructure(rawLower, blob) {
  const needles = [
    "fetch failed",
    "failed to fetch",
    "network error",
    "load failed",
    "networkrequestfailed",
    "econnreset",
    "etimedout",
    "econnrefused",
    "enotfound",
    "sin cuerpo de respuesta",
    "respuesta incompleta del servidor",
    "respuesta incompleta",
    "error leyendo la respuesta",
    "typeerror: failed to fetch",
    "socket hang up",
  ];
  if (needles.some((n) => rawLower.includes(n) || blob.includes(n))) {
    return true;
  }
  if (/\b502\b|\b503\b|\b504\b/.test(blob)) return true;
  if (blob.includes("bad gateway") || blob.includes("gateway timeout")) {
    return true;
  }
  return false;
}

/**
 * Mensaje legible cuando falla la red o el SAIJ (evita dejar solo "fetch failed" en rojo).
 * @param {import('vue').default} vm componente con $t
 * @param {string} rawMessage mensaje del catch o del emit error
 * @param {unknown} [originalErr] Error de fetch/axios
 */
export function resolveJurisInfrastructureErrorText(vm, rawMessage, originalErr) {
  const raw = rawMessage != null ? String(rawMessage).trim() : "";
  const rawLower = raw.toLowerCase();
  const blob = `${rawLower} ${jurisErrorBlobFromUnknown(originalErr)}`.trim();

  if (!raw && !blob) {
    return vm.$t("jurisprudencia.error");
  }

  if (
    /tiempo de espera|timeout|agotado|timed out|time-out/i.test(raw) ||
    /tiempo de espera|timeout|agotado|timed out/i.test(blob)
  ) {
    return vm.$t("jurisprudencia.chatErrorTimeoutSaij");
  }

  if (jurisMessageLooksLikeInfrastructure(rawLower, blob)) {
    return vm.$t("jurisprudencia.chatErrorNetworkOrSaij");
  }

  return raw || vm.$t("jurisprudencia.error");
}

/**
 * Texto traducido para una línea de estado del backend (evt incluye step y campos).
 */
export function formatJurisSearchProgress(
  vm,
  evt,
  keyPrefix = "jurisprudencia.progress"
) {
  const step = evt && evt.step;
  if (!step) return "";
  const key = `${keyPrefix}.${step}`;
  if (!vm.$te(key)) return "";
  const queriesStr = Array.isArray(evt.queries)
    ? evt.queries
        .slice(0, 4)
        .map((q) => String(q || "").trim())
        .filter(Boolean)
        .join(" · ")
    : "";
  return vm.$t(key, {
    preview: evt.preview || "",
    sampleTitle: evt.sampleTitle || "",
    count: evt.count != null ? evt.count : "",
    round: evt.round != null ? evt.round : "",
    need: evt.need != null ? evt.need : "",
    have: evt.have != null ? evt.have : "",
    offset: evt.offset != null ? evt.offset : "",
    queryPreview: evt.queryPreview || "",
    accepted: evt.accepted != null ? evt.accepted : "",
    target: evt.target != null ? evt.target : "",
    queries: queriesStr,
  });
}
